import { BaseComponent } from '../shared/base-component.js'

class WeightLossCalculator extends BaseComponent {
	#revealObserver = null
	#lossCountRaf = 0
	#hasPlayedLossIntro = false
	#isLossCounting = false
	#currentLossRounded = 0

	constructor() {
		super()
		this.attachShadow({ mode: 'open' })
	}

	connectedCallback() {
		this.config = this.getConfig()
		this.render()
		this.cacheElements()
		this.attachEvents()
		this.updateUI(this.config.defaultWeight)
		// Pin the marker dot near the end of the curve (not at the very
		// tip — leaves some visible curve to the right of the dot). The
		// graph illustrates the projected outcome (lossPercentage), so the
		// dot stays fixed and only the lbs readout below changes.
		this.updateDial(0.85)
		this.initGraphReveal()
	}

	getConfig() {
		const minWeight = Math.max(1, Number(this.dataset.minWeight || 160))
		const maxWeight = Math.max(minWeight, Number(this.dataset.maxWeight || 500))
		const defaultWeight = Math.min(maxWeight, Math.max(minWeight, Number(this.dataset.defaultWeight || 220)))

		return {
			title: this.dataset.title || 'Weight Loss Calculator',
			currentWeightLabel: this.dataset.currentWeightLabel || 'Your current weight',
			projectedLabel: this.dataset.projectedLabel || '',
			minWeight,
			maxWeight,
			defaultWeight,
			weightStep: Math.max(1, Number(this.dataset.weightStep || 1)),
			months: Math.max(1, Number(this.dataset.months || 3)),
			lossPercentage: Math.max(0, Number(this.dataset.lossPercentage || 6.4)),
		}
	}

	render() {
		this.shadowRoot.innerHTML = `
			<style>
				:host {
					display: block;
					${this.getThemeStyles()}
				}

				* {
					box-sizing: border-box;
				}

				.calculator {
					display: flex;
					flex-direction: column;
					gap: var(--spacing-20);
				}

				/* Top: current weight row + slider */
				.current-row {
					display: flex;
					justify-content: space-between;
					align-items: center;
					gap: var(--spacing-12);
					margin-bottom: var(--spacing-12);
				}

				.current-label,
				.current-value {
					margin: 0;
					font-family: var(--font-matter);
					font-size: var(--font-size-body, 1rem);
					font-weight: 400;
					line-height: 1.3;
					color: var(--color-slate-40);
				}

				.slider {
					-webkit-appearance: none;
					appearance: none;
					width: 100%;
					height: 0.75rem;
					border-radius: 999px;
					touch-action: pan-y;
					background: linear-gradient(
						to right,
						#1F64AD 0%,
						#1F64AD var(--slider-progress, 50%),
						#FFFFFF var(--slider-progress, 50%),
						#FFFFFF 100%
					);
					outline: none;
				}

				.slider::-webkit-slider-thumb {
					-webkit-appearance: none;
					appearance: none;
					width: 1.875rem;
					height: 1.875rem;
					border-radius: 999px;
					border: 1px solid var(--color-slate-90);
					background: var(--color-white);
					box-shadow: 0 3.5px 7px rgba(49, 48, 47, 0.08);
					cursor: pointer;
				}

				.slider::-moz-range-thumb {
					width: 1.875rem;
					height: 1.875rem;
					border-radius: 999px;
					border: 1px solid var(--color-slate-90);
					background: var(--color-white);
					box-shadow: 0 3.5px 7px rgba(49, 48, 47, 0.08);
					cursor: pointer;
				}

				/* Middle: curve graph */
				.graph {
					position: relative;
					aspect-ratio: 293 / 187;
					margin: 0 calc(var(--spacing-32) * -1);
				}

				.dial {
					width: 100%;
					height: 100%;
					display: block;
				}

				.area-fill {
					fill: url(#curveAreaGradient);
					opacity: 0;
					transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.15s;
				}

				.curve-line {
					fill: none;
					stroke: #1C7CE3;
					stroke-width: 1.5;
					stroke-linecap: round;
					stroke-dasharray: var(--curve-length, 0);
					stroke-dashoffset: var(--curve-offset, 0);
					transition: stroke-dashoffset var(--graph-draw-duration, 0.9s) cubic-bezier(0.4, 0, 0.2, 1);
				}

				:host(.is-graph-animated) .area-fill {
					opacity: 1;
				}

				.marker {
					position: absolute;
					left: var(--marker-x, 0%);
					top: var(--marker-y, 0%);
					transform: translate(-50%, -50%);
					display: flex;
					align-items: center;
					justify-content: center;
					pointer-events: none;
				}

				.marker-dot {
					width: 0.8125rem;
					height: 0.8125rem;
					border-radius: 999px;
					background: #5EA1ED;
					box-shadow: 0 0 0 7px rgba(255, 255, 255, 0.64);
					opacity: 0;
					transform: scale(0.8);
					transition:
						opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1) var(--graph-draw-duration, 0.9s),
						transform 0.25s cubic-bezier(0.4, 0, 0.2, 1) var(--graph-draw-duration, 0.9s);
				}

				:host(.is-graph-animated) .marker-dot {
					opacity: 1;
					transform: scale(1);
				}

				/* Bottom: "You could lose Xlbs" */
				.loss {
					display: flex;
					flex-direction: column;
					align-items: center;
					gap: var(--spacing-12);
				}

				.loss-label {
					margin: 0;
					font-family: var(--font-matter);
					font-size: var(--font-size-body, 1rem);
					font-weight: 400;
					line-height: 1.3;
					color: var(--color-slate-40);
				}

				.loss-amount {
					margin: 0;
					font-family: var(--font-matter);
					font-size: 2.625rem;
					font-weight: 500;
					line-height: 1.08;
					letter-spacing: -0.01em;
					color: var(--color-slate-20);
				}

				.note {
					margin: 0;
					text-align: center;
					font-family: var(--font-matter);
					font-size: 0.75rem;
					line-height: 1.35;
					color: var(--color-slate-40);
				}

				@media (prefers-reduced-motion: reduce) {
					.area-fill,
					.curve-line,
					.marker-dot {
						transition: none !important;
					}
				}
			</style>
			<div class="calculator">
				<div class="current-row">
					<p class="current-label">${this.escapeHtml(this.config.currentWeightLabel)}</p>
					<p class="current-value" data-current-weight>${this.config.defaultWeight}lbs</p>
				</div>

				<div class="slider-wrap">
					<input
						class="slider"
						type="range"
						min="${this.config.minWeight}"
						max="${this.config.maxWeight}"
						step="${this.config.weightStep}"
						value="${this.config.defaultWeight}"
						aria-label="Current weight slider"
					/>
				</div>

				<div class="graph">
					<svg class="dial" viewBox="0 0 293 187" aria-hidden="true">
						<defs>
							<linearGradient id="curveAreaGradient" x1="0" y1="20" x2="0" y2="187" gradientUnits="userSpaceOnUse">
								<stop offset="0%" stop-color="rgba(28, 124, 227, 0.18)"></stop>
								<stop offset="100%" stop-color="rgba(255, 255, 255, 0)"></stop>
							</linearGradient>
						</defs>
						<path class="area-fill" d="M0 20 C70 150 200 150 293 150 L293 187 L0 187 Z"></path>
						<path class="curve-line" data-curve-path d="M0 20 C70 150 200 150 293 150"></path>
					</svg>
					<div class="marker" data-marker>
						<span class="marker-dot"></span>
					</div>
				</div>

				<div class="loss">
					<p class="loss-label">You could lose</p>
					<p class="loss-amount" data-loss-int>0lbs</p>
				</div>

				${this.config.projectedLabel ? `<p class="note">${this.escapeHtml(this.config.projectedLabel)} · ${this.config.months} months at ${this.config.lossPercentage}%</p>` : ''}
			</div>
		`
	}

	cacheElements() {
		this.range = this.shadowRoot.querySelector('.slider')
		this.currentWeightEl = this.shadowRoot.querySelector('[data-current-weight]')
		this.projectedWeightEl = this.shadowRoot.querySelector('[data-projected-weight]')
		this.lossIntEl = this.shadowRoot.querySelector('[data-loss-int]')
		this.curvePath = this.shadowRoot.querySelector('[data-curve-path]')
		this.markerEl = this.shadowRoot.querySelector('[data-marker]')

		if (this.curvePath) {
			this.pathLength = this.curvePath.getTotalLength()
		}
	}

	disconnectedCallback() {
		if (this.#revealObserver) {
			this.#revealObserver.disconnect()
			this.#revealObserver = null
		}
		this.cancelLossCountUp()
	}

	attachEvents() {
		if (this.range) {
			this.range.addEventListener('input', (event) => {
				this.cancelLossCountUp()
				this.#hasPlayedLossIntro = true
				const value = Number(event.target.value)
				this.updateUI(value)

				// This one renders a figure from load off a default weight, so a
				// completion on boot would count every impression. It fires on the
				// first slider move instead — the point the number becomes theirs.
				// The weight itself never travels.
				if (!this._hasCalculated) {
					window.felixTrack?.('calculator_complete', { calculatorName: 'weight_loss_calculator' })
					this._hasCalculated = true
				}
			})
		}
	}

	isReducedMotionPreferred() {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches
	}

	renderLossInt(value) {
		if (!this.lossIntEl) {
			return
		}
		this.lossIntEl.textContent = `${Math.max(0, Math.round(value))}lbs`
	}

	cancelLossCountUp() {
		if (this.#lossCountRaf) {
			cancelAnimationFrame(this.#lossCountRaf)
			this.#lossCountRaf = 0
		}
		this.#isLossCounting = false
	}

	startLossCountUp(targetValue) {
		const target = Math.max(0, Math.round(targetValue))
		this.cancelLossCountUp()
		this.#isLossCounting = true
		this.renderLossInt(0)

		const duration = 900
		const startedAt = performance.now()

		const tick = (now) => {
			const progress = Math.min(1, (now - startedAt) / duration)
			const value = Math.round(target * progress)
			this.renderLossInt(value)

			if (progress < 1) {
				this.#lossCountRaf = requestAnimationFrame(tick)
				return
			}

			this.#lossCountRaf = 0
			this.#isLossCounting = false
			this.#hasPlayedLossIntro = true
			this.renderLossInt(target)
		}

		this.#lossCountRaf = requestAnimationFrame(tick)
	}

	initGraphReveal() {
		if (!this.curvePath || !Number.isFinite(this.pathLength)) {
			return
		}

		this.style.setProperty('--curve-length', `${this.pathLength}`)

		if (this.isReducedMotionPreferred()) {
			this.style.setProperty('--curve-offset', '0')
			this.classList.add('is-graph-animated')
			this.#hasPlayedLossIntro = true
			this.renderLossInt(this.#currentLossRounded)
			return
		}

		this.style.setProperty('--curve-offset', `${this.pathLength}`)
		this.classList.remove('is-graph-animated')

		this.#revealObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						return
					}

					requestAnimationFrame(() => {
						this.style.setProperty('--curve-offset', '0')
						this.classList.add('is-graph-animated')
						this.startLossCountUp(this.#currentLossRounded)
					})

					this.#revealObserver?.disconnect()
					this.#revealObserver = null
				})
			},
			{
				root: null,
				// Start reveal earlier so the graph doesn't feel delayed while scrolling.
				rootMargin: '0px 0px -10% 0px',
				threshold: 0.35,
			}
		)

		this.#revealObserver.observe(this)
	}

	clampWeight(value) {
		if (!Number.isFinite(value)) {
			return this.config.defaultWeight
		}
		return Math.min(this.config.maxWeight, Math.max(this.config.minWeight, value))
	}

	updateUI(weight) {
		const currentWeight = this.clampWeight(weight)
		const lossAmount = currentWeight * (this.config.lossPercentage / 100)
		const projectedWeight = currentWeight - lossAmount
		const progress = (currentWeight - this.config.minWeight) / (this.config.maxWeight - this.config.minWeight || 1)
		this.#currentLossRounded = Math.round(lossAmount)

		if (this.range && Number(this.range.value) !== currentWeight) {
			this.range.value = String(currentWeight)
		}
		this.updateSliderFill(progress)

		if (this.currentWeightEl) {
			this.currentWeightEl.textContent = `${currentWeight.toFixed(0)}lbs`
		}
		if (this.lossIntEl && !this.#isLossCounting) {
			if (!this.#hasPlayedLossIntro && !this.isReducedMotionPreferred()) {
				this.renderLossInt(0)
			} else {
				this.renderLossInt(this.#currentLossRounded)
			}
		}

		// Marker dot stays pinned at the end of the curve (set once in
		// connectedCallback) — the graph indicates the lossPercentage
		// outcome, not the slider position.
	}

	updateSliderFill(progress) {
		if (!this.range) return
		const clampedProgress = Math.min(1, Math.max(0, progress))
		const trackWidth = this.range.getBoundingClientRect().width
		const thumbSize = 32

		if (trackWidth > thumbSize) {
			const centerPx = thumbSize / 2 + clampedProgress * (trackWidth - thumbSize)
			const centerPct = (centerPx / trackWidth) * 100
			this.range.style.setProperty('--slider-progress', `${centerPct}%`)
			return
		}

		this.range.style.setProperty('--slider-progress', `${clampedProgress * 100}%`)
	}

	updateDial(progress) {
		if (!this.curvePath || !this.markerEl || !Number.isFinite(this.pathLength)) {
			return
		}

		// Keep marker on-curve but prevent edge clipping at card boundaries.
		const minProgress = 0.075
		const maxProgress = 0.925
		const clampedProgress = Math.min(maxProgress, Math.max(minProgress, progress))
		const distance = this.pathLength * clampedProgress
		const point = this.curvePath.getPointAtLength(distance)
		this.markerEl.style.setProperty('--marker-x', `${(point.x / 293) * 100}%`)
		this.markerEl.style.setProperty('--marker-y', `${(point.y / 187) * 100}%`)
	}

	escapeHtml(str) {
		return String(str)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;')
	}
}

if (!customElements.get('felix-weight-loss-calculator')) {
	customElements.define('felix-weight-loss-calculator', WeightLossCalculator)
}
